import type { ReactElement } from "react";
import { Button } from "../../atoms/Button/Button.js";
import { cn } from "../../lib/utils.js";

export interface ProgressiveListFooterProps {
  readonly loadedCount: number;
  readonly totalCount?: number | undefined;
  readonly itemLabel?: string | undefined;
  readonly singularItemLabel?: string | undefined;
  readonly loadMoreLabel?: string | undefined;
  readonly hasMore: boolean;
  readonly busy: boolean;
  readonly error?: string | null | undefined;
  readonly onLoadMore: () => void;
  readonly onRetry?: (() => void) | undefined;
  readonly className?: string | undefined;
}

/** Consistent status and controls for compact lists that retain loaded pages. */
export function ProgressiveListFooter({
  loadedCount,
  totalCount,
  itemLabel = "items",
  singularItemLabel,
  loadMoreLabel = "Load more",
  hasMore,
  busy,
  error,
  onLoadMore,
  onRetry,
  className,
}: ProgressiveListFooterProps): ReactElement {
  const displayedCount = totalCount ?? loadedCount;
  const displayedItemLabel =
    displayedCount === 1 && singularItemLabel ? singularItemLabel : itemLabel;
  const count =
    totalCount === undefined
      ? `${loadedCount} ${displayedItemLabel} loaded`
      : `${loadedCount} of ${totalCount} ${displayedItemLabel} loaded`;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-2 border-t border-pb-border pt-2",
        className,
      )}
    >
      <div className="min-w-0 text-xs">
        <span
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="text-pb-text-muted"
        >
          {busy ? `Loading more ${itemLabel}…` : count}
        </span>
        {error ? (
          <span role="alert" className="ms-2 text-pb-red-text">
            {error}
          </span>
        ) : null}
      </div>
      {error && onRetry ? (
        <Button size="md" variant="outline" onClick={onRetry}>
          Try again
        </Button>
      ) : hasMore ? (
        <Button size="md" variant="outline" loading={busy} onClick={onLoadMore}>
          {loadMoreLabel}
        </Button>
      ) : null}
    </div>
  );
}
